"use client";

import { useState } from "react";
import {
  Download,
  Save,
  FileText,
  Heart,
  BarChart3,
  RefreshCw,
  Users,
  TrendingUp,
  Target,
  Zap,
  User,
  Award,
  Palette,
} from "lucide-react";
import "./user-team-detailed-report.css";

const UserTeamDetailedReport = ({ comparisonData }) => {
  console.log(comparisonData, "comparisonData from <UserTeamDetailedReport />");
  const [activeSection, setActiveSection] = useState("overview");
  const [activeSubTab, setActiveSubTab] = useState("analysis");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [filename, setFilename] = useState("");

  // Utility function to convert value to array
  const toArray = (v) =>
    Array.isArray(v)
      ? v
      : typeof v === "string"
      ? v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  // Dummy data structure based on the specification
  const userTeamData = {
    title: "User vs Team Analysis Report",
    team: {
      name: `${comparisonData.detailedReport.teamDetails.name}`,
      size: `${comparisonData.detailedReport.teamDetails.size}`,
      department: `${comparisonData.detailedReport.teamDetails.department}`,
      teamLead: `${comparisonData.detailedReport.teamDetails.teamLead}`,
      avgPerformance: `${comparisonData.detailedReport.teamDetails.avgLeadershipScore.toFixed(
        1
      )}`,
      leadershipReady: `${comparisonData.detailedReport.teamDetails.leadershipReadyCount}`,
      description: `${comparisonData.detailedReport.teamDetails.teamDescription}`,
      teamCulture: `${comparisonData.detailedReport.teamDetails.teamCulture}`,
      missingSkills: `${comparisonData.detailedReport.overview.teamData.skillsMissing}`,
      requiredSkills: {
        Communication: 7,
        Creativity: 6,
        Leadership: 5,
        "Problem Solving": 8,
        "Time Management": 7,
        Adaptability: 6,
        Teamwork: 8,
        "Critical Thinking": 7,
        "Emotional Intelligence": 6,
        Innovation: 5,
      },
      teamMembers: [
        {
          name: "John",
          leadership_score: 60,
          strengths: [],
          weaknesses: ["Strategic Vision", "Decision-Making"],
        },
        {
          name: "Maria",
          leadership_score: 70,
          strengths: ["Adaptability"],
          weaknesses: ["Decision-Making"],
        },
        {
          name: "David",
          leadership_score: 90,
          strengths: ["Communication", "Collaboration", "Decision-Making"],
          weaknesses: [],
        },
        {
          name: "Sarah",
          leadership_score: 65,
          strengths: ["Communication"],
          weaknesses: ["Strategic Vision"],
        },
        {
          name: "Mike",
          leadership_score: 72,
          strengths: ["Problem Solving"],
          weaknesses: ["Collaboration"],
        },
        {
          name: "Lisa",
          leadership_score: 68,
          strengths: ["Adaptability", "Communication"],
          weaknesses: ["Decision-Making"],
        },
        {
          name: "Tom",
          leadership_score: 75,
          strengths: ["Strategic Vision"],
          weaknesses: ["Collaboration"],
        },
        {
          name: "Anna",
          leadership_score: 58,
          strengths: ["Communication"],
          weaknesses: ["Strategic Vision", "Decision-Making"],
        },
      ],
      culture: {
        Spirit: { avgScore: 6, minScore: 5, maxScore: 7 },
        Profession: { avgScore: 8, minScore: 7, maxScore: 9 },
        Rewards: { avgScore: 5, minScore: 4, maxScore: 6 },
        Purpose: { avgScore: 7, minScore: 6, maxScore: 8 },
      },
    },

    user: {
      name: `${comparisonData.detailedReport.userDetails.name}`,
      skills: {
        Communication: 8,
        Creativity: 4,
        Leadership: 6,
        "Problem Solving": 9,
        "Time Management": 8,
        Adaptability: 7,
        Teamwork: 7,
        "Critical Thinking": 8,
        "Emotional Intelligence": 6,
        Innovation: 5,
      },
      strengths: toArray(
        comparisonData.detailedReport.leadershipContrast.visualization.userData
          .strengths
      ),
      weaknesses: toArray(
        comparisonData.detailedReport.leadershipContrast.visualization.userData
          .weaknesses
      ),
      culture: {
        Spirit: 7,
        Profession: 6,
        Rewards: 8,
        Purpose: 6,
      },
      // strengths: `${comparisonData.detailedReport.leadershipContrast.visualization.userData.strengths}`,
      strengths: toArray(
        comparisonData?.detailedReport?.leadershipContrast?.visualization
          ?.userData?.strengths
      ),
      weaknesses: ["Collaboration"],
      culture: {
        Spirit: 7,
        Profession: 6,
        Rewards: 8,
        Purpose: 6,
      },
    },
    fitScore: 78,
    analysis: {
      softSkills: `${comparisonData.detailedReport.summary.skillGaps}`,
      leadership: `${comparisonData.detailedReport.summary.leadership}`,
      culture: `${comparisonData.detailedReport.summary.cultureFit}`,
    },
  };

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "userTeamDetails", label: "User and Team Details", icon: Users },
    {
      id: "softSkillsComplementarity",
      label: "Soft Skills Complementarity",
      icon: Heart,
    },
    { id: "leadershipContrast", label: "Leadership Contrast", icon: Award },
    { id: "teamCultureFit", label: "Team Culture Fit", icon: Palette },
    { id: "summary", label: "Summary", icon: FileText },
  ];

  const renderSubTabs = (sectionId) => {
    if (
      [
        "softSkillsComplementarity",
        "leadershipContrast",
        "teamCultureFit",
      ].includes(sectionId)
    ) {
      const labels = {
        softSkillsComplementarity: ["Analysis", "Soft Skills"],
        leadershipContrast: ["Analysis", "Leadership Potential"],
        teamCultureFit: ["Analysis", "Team Culture Fit"],
      };

      return (
        <div className="ut-detailed-sub-tabs">
          <button
            className={`ut-detailed-sub-tab ${
              activeSubTab === "analysis" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("analysis")}
          >
            {labels[sectionId][0]}
          </button>
          <button
            className={`ut-detailed-sub-tab ${
              activeSubTab === "visualization" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("visualization")}
          >
            {labels[sectionId][1]}
          </button>
        </div>
      );
    }
    return null;
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="ut-detailed-section-content">
            <div className="ut-detailed-overview-grid">
              {/* Team Name */}
              <div className="ut-detailed-overview-card ut-detailed-primary">
                <div className="ut-detailed-overview-icon">
                  <Users size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Team</h3>
                  <div className="ut-detailed-score-display">
                    {userTeamData.team.name}
                  </div>
                  <p>Team name</p>
                </div>
              </div>

              {/* User Name */}
              <div className="ut-detailed-overview-card ut-detailed-secondary">
                <div className="ut-detailed-overview-icon">
                  <User size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>User</h3>
                  <div className="ut-detailed-score-display">
                    {userTeamData.user.name}
                  </div>
                  <p>User name</p>
                </div>
              </div>

              {/* Missing Team Soft Skills */}
              <div className="ut-detailed-overview-card ut-detailed-accent">
                <div className="ut-detailed-overview-icon">
                  <Target size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Missing Team Soft Skills</h3>
                  <div className="ut-detailed-score-display">
                    {
                      comparisonData.detailedReport.overview.teamData
                        .skillsMissing.length
                    }{" "}
                    Skills
                  </div>
                  <p>{userTeamData.team.name}</p>
                </div>
              </div>

              {/* User Soft Skills */}
              {/*<div className="ut-detailed-overview-card ut-detailed-success">
                <div className="ut-detailed-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>User Skills</h3>
                  <div className="ut-detailed-score-display">{Object.keys(userTeamData.user.skills).length}</div>
                  <p>User soft skills (count) - {userTeamData.user.name}</p>
                </div>
              </div>*/}

              {/* User-Team Fit Score */}
              <div className="ut-detailed-overview-card ut-detailed-dark">
                <div className="ut-detailed-overview-icon">
                  <Target size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Fit Score</h3>
                  <div className="ut-detailed-score-display">
                    {comparisonData.detailedReport.overview.userData.teamFit}%
                  </div>
                  <p>User-team fit score</p>
                </div>
              </div>

              {/* Leadership Score - User */}
              <div className="ut-detailed-overview-card ut-detailed-info">
                <div className="ut-detailed-overview-icon">
                  <Award size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Leadership Score</h3>
                  <div className="ut-detailed-score-display">
                    {
                      comparisonData.detailedReport.overview.userData
                        .leadershipScore
                    }
                    %
                  </div>
                  <p>{userTeamData.user.name}</p>
                </div>
              </div>

              {/* Average Leadership - Team */}
              <div className="ut-detailed-overview-card ut-detailed-warning">
                <div className="ut-detailed-overview-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Team Average Leadership</h3>
                  <div className="ut-detailed-score-display">
                    {
                      comparisonData.detailedReport.overview.teamData
                        .avgLeadershipScore
                    }
                    %
                  </div>
                  <p>{userTeamData.team.name}</p>
                </div>
              </div>

              {/* Leadership Ready Count */}
              <div className="ut-detailed-overview-card ut-detailed-danger">
                <div className="ut-detailed-overview-icon">
                  <Zap size={24} />
                </div>
                <div className="ut-detailed-overview-content">
                  <h3>Leadership Ready</h3>
                  <div className="ut-detailed-score-display">
                    {
                      comparisonData.detailedReport.overview.teamData
                        .leadershipReadyCount
                    }{" "}
                    Member(s)
                  </div>
                  <p>{userTeamData.team.name}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "userTeamDetails":
        return (
          <div className="ut-detailed-section-content">
            <h3 style={{ marginBottom: "10px" }}>User and Team Details</h3>
            <p style={{ marginBottom: "20px" }}>
              Comprehensive profiles including personal details, performance
              metrics, and team information.
            </p>
            <div className="ut-detailed-details-grid">
              {/* User Card */}
              <div className="ut-detailed-details-card">
                <div className="ut-detailed-details-header">
                  <h4>{userTeamData.user.name}</h4>
                </div>
                <div className="ut-detailed-details-info">
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Role:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.role}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Department:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.department}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">
                      Performance Score:
                    </span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.performanceScore}%
                    </span>
                  </div>
                  {/* <div className="ut-detailed-info-item"> */}
                  {/* <span className="ut-detailed-info-label">
                      Team Assigned:
                    </span> */}
                  {/* <span className="ut-detailed-info-value">
                      {userTeamData.user.teamAssigned}
                    </span> */}
                  {/* </div> */}
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Education:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.education}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Experience:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.experience}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">
                      Leadership Score:
                    </span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.user.leadershipScore}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Team Card */}
              <div className="ut-detailed-details-card">
                <div className="ut-detailed-details-header">
                  <h4>{userTeamData.team.name}</h4>
                </div>
                <div className="ut-detailed-details-info">
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Size:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.team.size} people
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Department:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.team.department}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">Team Lead:</span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.team.teamLead}
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">
                      Avg Performance:
                    </span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.team.avgPerformance}%
                    </span>
                  </div>
                  <div className="ut-detailed-info-item">
                    <span className="ut-detailed-info-label">
                      Leadership Ready:
                    </span>
                    <span className="ut-detailed-info-value">
                      {userTeamData.team.leadershipReady}
                    </span>
                  </div>
                </div>
                <div className="ut-detailed-team-description">
                  <h5>Team Culture</h5>
                  <p>{userTeamData.team.teamCulture}</p>
                </div>
                <div className="ut-detailed-team-description">
                  <h5>Team Description</h5>
                  <p>{userTeamData.team.description}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "softSkillsComplementarity":
        return (
          <div className="ut-detailed-section-content">
            {renderSubTabs("softSkillsComplementarity")}
            {activeSubTab === "analysis" ? (
              <div className="ut-detailed-analysis-container">
                <h3>Soft Skills Complementarity Analysis</h3>
                <div className="ut-detailed-analysis-content">
                  <p>
                    {
                      comparisonData.detailedReport.softSkillsComplementarity
                        .analysis
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Soft Skills Comparison</h3>
                <p style={{ marginBottom: "20px" }}>
                  Comparison of all 10 soft skills required for the team and
                  values user has, with missing team soft skills marked.
                </p>
                <div className="ut-detailed-skills-grid">
                  {comparisonData.detailedReport.softSkillsComplementarity.visualization.skillComparison.map(
                    ({ skill, teamNeed, userHas, isMissing }) => {
                      const meetsRequirement = userHas >= teamNeed;

                      return (
                        <div key={skill} className="ut-detailed-skill-card">
                          <div className="ut-detailed-skill-header">
                            <h4 style={{ marginBottom: "8px" }}>
                              {skill}
                              {isMissing && (
                                <span className="ut-detailed-missing-indicator">
                                  MISSING
                                </span>
                              )}
                            </h4>
                          </div>

                          <div className="ut-detailed-comparison-bars">
                            {/* Team Need */}
                            <div className="ut-detailed-skill-bar">
                              <span className="ut-detailed-skill-label">
                                Team Need
                              </span>
                              <div className="ut-detailed-progress-container">
                                <div className="ut-detailed-progress-bar">
                                  <div
                                    className="ut-detailed-progress-fill ut-detailed-team-need"
                                    style={{
                                      width: `${(teamNeed / 10) * 100}%`,
                                    }}
                                  />
                                </div>
                                <span className="ut-detailed-score">
                                  {teamNeed}
                                </span>
                              </div>
                            </div>

                            {/* User Has */}
                            <div className="ut-detailed-skill-bar">
                              <span
                                className={`ut-detailed-skill-label ${
                                  meetsRequirement
                                    ? "ut-detailed-label-meets"
                                    : "ut-detailed-label-below"
                                }`}
                              >
                                User Has
                              </span>
                              <div className="ut-detailed-progress-container">
                                <div className="ut-detailed-progress-bar">
                                  <div
                                    className={`ut-detailed-progress-fill ${
                                      meetsRequirement
                                        ? "ut-detailed-user-meets"
                                        : "ut-detailed-user-below"
                                    }`}
                                    style={{
                                      width: `${(userHas / 10) * 100}%`,
                                    }}
                                  />
                                </div>
                                <span className="ut-detailed-score">
                                  {userHas}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "leadershipContrast":
        return (
          <div className="ut-detailed-section-content">
            {renderSubTabs("leadershipContrast")}
            {activeSubTab === "analysis" ? (
              <div className="ut-detailed-analysis-container">
                <h3>Leadership Contrast Analysis</h3>
                <div className="ut-detailed-analysis-content">
                  <p>
                    {comparisonData.detailedReport.leadershipContrast.analysis}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Leadership Potential</h3>
                <p style={{ marginBottom: "20px" }}>
                  Leadership score, strengths and weaknesses comparison for all
                  team members and the user.
                </p>

                <h4 style={{ marginBottom: "20px" }}>
                  People - {userTeamData.user.name}
                </h4>

                {/* User Leadership Profile */}
                <div className="ut-detailed-leadership-card ut-detailed-user-card">
                  <div className="ut-detailed-leadership-header">
                    <h4>{userTeamData.user.name} (User)</h4>
                    <div className="ut-detailed-leadership-score">
                      {
                        comparisonData.detailedReport.leadershipContrast
                          .visualization.userData.leadershipScore
                      }
                      %
                    </div>
                  </div>
                  <div className="ut-detailed-leadership-details">
                    <div className="ut-detailed-strengths">
                      <h5>Strengths</h5>
                      {/* <div className="ut-detailed-skill-tags">
                        {userTeamData.user.strengths.map((strength, idx) => (
                          <span
                            key={idx}
                            className="ut-detailed-skill-tag ut-detailed-strength-tag"
                          >
                            {strength}
                          </span>
                        ))}
                      </div> */}

                      <div className="ut-detailed-skill-tags">
                        {userTeamData.user.strengths.length ? (
                          userTeamData.user.strengths.map((strength, idx) => (
                            <span
                              key={idx}
                              className="ut-detailed-skill-tag ut-detailed-strength-tag"
                            >
                              {strength}
                            </span>
                          ))
                        ) : (
                          <span className="ut-detailed-no-items">
                            No strengths found
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ut-detailed-weaknesses">
                      <h5>Weaknesses</h5>
                      <div className="ut-detailed-skill-tags">
                        {comparisonData.detailedReport.leadershipContrast
                          .visualization.userData.weaknesses.length ? (
                          comparisonData.detailedReport.leadershipContrast.visualization.userData.weaknesses.map(
                            (weakness, idx) => (
                              <span
                                key={idx}
                                className="ut-detailed-skill-tag ut-detailed-weakness-tag"
                              >
                                {weakness}
                              </span>
                            )
                          )
                        ) : (
                          <span className="ut-detailed-no-items">
                            No weaknesses found
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <h4 style={{ margin: "20px 0" }}>Team Members</h4>

                <div className="ut-detailed-leadership-grid">
                  {/* Team Members Leadership Profiles */}
                  {comparisonData.detailedReport.leadershipContrast
                    .visualization.teamMembers.length ? (
                    comparisonData.detailedReport.leadershipContrast.visualization.teamMembers.map(
                      (member, index) => (
                        <div
                          key={index}
                          className="ut-detailed-leadership-card"
                        >
                          <div className="ut-detailed-leadership-header">
                            <h4>{member.name}</h4>
                            <div className="ut-detailed-leadership-score">
                              {member.leadership_score}%
                            </div>
                          </div>
                          <div className="ut-detailed-leadership-details">
                            <div className="ut-detailed-strengths">
                              <h5>Strengths</h5>
                              <div className="ut-detailed-skill-tags">
                                {member.strengths.length > 0 ? (
                                  member.strengths.map((strength, idx) => (
                                    <span
                                      key={idx}
                                      className="ut-detailed-skill-tag ut-detailed-strength-tag"
                                    >
                                      {strength}
                                    </span>
                                  ))
                                ) : (
                                  <span className="ut-detailed-no-items">
                                    No strengths found
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="ut-detailed-weaknesses">
                              <h5>Weaknesses</h5>
                              <div className="ut-detailed-skill-tags">
                                {member.weaknesses.length > 0 ? (
                                  member.weaknesses.map((weakness, idx) => (
                                    <span
                                      key={idx}
                                      className="ut-detailed-skill-tag ut-detailed-weakness-tag"
                                    >
                                      {weakness}
                                    </span>
                                  ))
                                ) : (
                                  <span className="ut-detailed-no-items">
                                    No weaknesses found
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <span className="ut-detailed-no-items">
                      No team members data found
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "teamCultureFit":
        return (
          <div className="ut-detailed-section-content">
            {renderSubTabs("teamCultureFit")}
            {activeSubTab === "analysis" ? (
              <div className="ut-detailed-analysis-container">
                <h3>Team Culture Fit Analysis</h3>
                <div className="ut-detailed-analysis-content">
                  <p>
                    {comparisonData.detailedReport.cultureFitAnalysis.analysis}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Team Culture Fit</h3>
                <p style={{ marginBottom: "20px" }}>
                  Personality skills comparison showing team ranges and user
                  values across four cultural dimensions.
                </p>
                <div className="ut-detailed-culture-grid">
                  {/* {Object.entries(userTeamData.team.culture).map(
                    ([dimension, teamData]) => {
                      const userValue = userTeamData.user.culture[dimension];
                      const isInRange =
                        userValue >= teamData.minScore &&
                        userValue <= teamData.maxScore;

                      return (
                        <div
                          key={dimension}
                          className="ut-detailed-culture-card"
                        >
                          <div className="ut-detailed-culture-header">
                            <h4>{dimension}</h4>
                            <div className="ut-detailed-culture-summary">
                              <span className="ut-detailed-team-range">
                                Team: {teamData.minScore}-{teamData.maxScore}{" "}
                                (avg: {teamData.avgScore})
                              </span>
                              <span
                                className={`ut-detailed-user-value ${
                                  isInRange
                                    ? "ut-detailed-in-range"
                                    : "ut-detailed-out-range"
                                }`}
                              >
                                User: {userValue}
                              </span>
                            </div>
                          </div>
                          <div className="ut-detailed-culture-visual">
                            <div className="ut-detailed-culture-scale">
                             
                              <div className="ut-detailed-scale-bar">
                                <div className="ut-detailed-scale-background"></div>
                                <div
                                  className="ut-detailed-team-range-bar"
                                  style={{
                                    left: `${(teamData.minScore / 10) * 100}%`,
                                    width: `${
                                      ((teamData.maxScore - teamData.minScore) /
                                        10) *
                                      100
                                    }%`,
                                  }}
                                ></div>
                                <div
                                  className="ut-detailed-team-avg-marker"
                                  style={{
                                    left: `${(teamData.avgScore / 10) * 100}%`,
                                  }}
                                ></div>
                                <div
                                  className={`ut-detailed-user-marker ${
                                    isInRange
                                      ? "ut-detailed-in-range"
                                      : "ut-detailed-out-range"
                                  }`}
                                  style={{ left: `${(userValue / 10) * 100}%` }}
                                ></div>
                              </div>
                              <div className="ut-detailed-scale-labels">
                                <span>0</span>
                                <span>10</span>
                              </div>
                            </div>
                            <div className="ut-detailed-culture-legend">
                              <div className="ut-detailed-legend-item">
                                <div className="ut-detailed-legend-color ut-detailed-team-range-color"></div>
                                <span>Team Range</span>
                              </div>
                              <div className="ut-detailed-legend-item">
                                <div className="ut-detailed-legend-color ut-detailed-team-avg-color"></div>
                                <span>Team Avg</span>
                              </div>
                              <div className="ut-detailed-legend-item">
                                <div
                                  className={`ut-detailed-legend-color ${
                                    isInRange
                                      ? "ut-detailed-user-in-color"
                                      : "ut-detailed-user-out-color"
                                  }`}
                                ></div>
                                <span>User</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )} */}

                  {comparisonData?.detailedReport?.cultureFitAnalysis?.visualization?.cultureFit?.map(
                    ({ aptitude, team, user }) => {
                      // Coerce to numbers and guard against bad data
                      const min = Number(team?.minScore ?? 0);
                      const max = Number(team?.maxScore ?? 0);
                      const avg = Number(team?.avgScore ?? 0);
                      const userValue = Number(user ?? 0);

                      const clamp01 = (v) => Math.max(0, Math.min(10, v));
                      const isInRange = userValue >= min && userValue <= max;

                      return (
                        <div
                          key={aptitude}
                          className="ut-detailed-culture-card"
                        >
                          <div className="ut-detailed-culture-header">
                            <h4>{aptitude}</h4>
                            <div className="ut-detailed-culture-summary">
                              <span className="ut-detailed-team-range">
                                Team: {min}-{max} (avg: {avg})
                              </span>
                              <span
                                className={`ut-detailed-user-value ${
                                  isInRange
                                    ? "ut-detailed-in-range"
                                    : "ut-detailed-out-range"
                                }`}
                              >
                                User: {userValue}
                              </span>
                            </div>
                          </div>

                          <div className="ut-detailed-culture-visual">
                            <div className="ut-detailed-culture-scale">
                              {/* Team Range Bar */}
                              <div className="ut-detailed-scale-bar">
                                <div className="ut-detailed-scale-background"></div>

                                <div
                                  className="ut-detailed-team-range-bar"
                                  style={{
                                    left: `${(clamp01(min) / 10) * 100}%`,
                                    width: `${
                                      ((clamp01(max) - clamp01(min)) / 10) * 100
                                    }%`,
                                  }}
                                ></div>

                                <div
                                  className="ut-detailed-team-avg-marker"
                                  style={{
                                    left: `${(clamp01(avg) / 10) * 100}%`,
                                  }}
                                ></div>

                                <div
                                  className={`ut-detailed-user-marker ${
                                    isInRange
                                      ? "ut-detailed-in-range"
                                      : "ut-detailed-out-range"
                                  }`}
                                  style={{
                                    left: `${(clamp01(userValue) / 10) * 100}%`,
                                  }}
                                ></div>
                              </div>

                              <div className="ut-detailed-scale-labels">
                                <span>0</span>
                                <span>10</span>
                              </div>
                            </div>

                            <div className="ut-detailed-culture-legend">
                              <div className="ut-detailed-legend-item">
                                <div className="ut-detailed-legend-color ut-detailed-team-range-color"></div>
                                <span>Team Range</span>
                              </div>
                              <div className="ut-detailed-legend-item">
                                <div className="ut-detailed-legend-color ut-detailed-team-avg-color"></div>
                                <span>Team Avg</span>
                              </div>
                              <div className="ut-detailed-legend-item">
                                <div
                                  className={`ut-detailed-legend-color ${
                                    isInRange
                                      ? "ut-detailed-user-in-color"
                                      : "ut-detailed-user-out-color"
                                  }`}
                                ></div>
                                <span>User</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case "summary":
        return (
          <div className="ut-detailed-section-content">
            <div className="ut-detailed-summary-content">
              <div className="ut-detailed-summary-section">
                <h4>Soft Skills Complementarity Summary</h4>
                <p>{userTeamData.analysis.softSkills}</p>
              </div>
              <div className="ut-detailed-summary-section">
                <h4>Leadership Contrast Summary</h4>
                <p>{userTeamData.analysis.leadership}</p>
              </div>
              <div className="ut-detailed-summary-section">
                <h4>Team Culture Fit Summary</h4>
                <p>{userTeamData.analysis.culture}</p>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  };

  const handleDownloadPDF = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `User vs Team Analysis Report\n\nGenerated on: ${new Date().toLocaleDateString()}`,
      ],
      {
        type: "text/plain",
      }
    );
    element.href = URL.createObjectURL(file);
    element.download = `user-team-analysis-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateDefaultFilename = () => {
    const date = new Date();
    const dateStr = date.toISOString().split("T")[0];
    return `user-team-analysis-report-${dateStr}`;
  };

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename();
    setFilename(defaultName);
    setShowFilenameModal(true);
  };

  const confirmSaveReport = () => {
    if (!filename.trim()) {
      alert("Please enter a filename");
      return;
    }
    setShowFilenameModal(false);
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveModal(true);
    }, 1500);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setActiveSubTab("analysis");
  };

  return (
    <div className="ut-detailed-comparison-page">
      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{userTeamData.title}</h1>
                <p>Comprehensive user-team compatibility and fit analysis</p>
              </div>
              <div
                className="detailed-header-actions"
                style={{ display: "none" }}
              >
                <button
                  className="detailed-action-btn detailed-secondary"
                  onClick={handleDownloadPDF}
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  className="detailed-action-btn detailed-primary"
                  onClick={handleSaveReport}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="ut-detailed-comparison-layout">
            {/* Navigation Sidebar */}
            <div className="ut-detailed-navigation">
              <div className="ut-detailed-nav-header">
                <h3>Report Sections</h3>
              </div>
              <nav className="ut-detailed-nav-menu">
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`ut-detailed-nav-item ${
                        activeSection === section.id ? "active" : ""
                      }`}
                      onClick={() => handleSectionChange(section.id)}
                    >
                      <IconComponent size={18} />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="ut-detailed-content">
              <div className="ut-detailed-content-header">
                <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
              </div>
              {renderSection()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserTeamDetailedReport;
